import CloseIcon from "@mui/icons-material/Close";
import { ThemeProvider, type Theme } from "@mui/material/styles";
import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import { createElement, Fragment, type ReactNode } from "react";
import { flushSync } from "react-dom";
import { createRoot, type Root } from "react-dom/client";
import {
  TutorialButton,
  TutorialButtonGroup,
  TutorialDisableButton,
  TutorialPopoverCloseButton,
  TutorialPopoverStyles,
} from "_shared/tutorials/tutorial-styled-components";
import type { TutorialCue } from "_shared/tutorials/tutorial-types";

export type TutorialDriver = {
  drive: () => void;
  destroy: () => void;
};

export type TutorialDriverOptions = {
  steps: readonly TutorialCue[];
  targets: ReadonlyMap<string, HTMLElement>;
  reducedMotion: boolean;
  theme: Theme;
  onDisableTutorials: () => void;
  onDestroyed: () => void;
};

type DriverInstance = TutorialDriver & {
  moveNext: () => void;
  movePrevious: () => void;
};

type TutorialPopover = {
  closeButton: HTMLButtonElement;
  footer: HTMLElement;
  footerButtons: HTMLElement;
  nextButton: HTMLButtonElement;
  previousButton: HTMLButtonElement;
};

function buttonLabel(button: HTMLButtonElement, fallback: string): string {
  return button.textContent?.trim() || fallback;
}

export function createTutorialDriver(options: TutorialDriverOptions): TutorialDriver {
  const driverFactory = driver as unknown as (configuration: Record<string, unknown>) => DriverInstance;
  let driverInstance: DriverInstance;
  let buttonRoots: Root[] = [];
  let disableRequested = false;

  const clearButtonRoots = () => {
    for (const root of buttonRoots) root.unmount();
    buttonRoots = [];
  };

  const schedule = (action: () => void) => globalThis.queueMicrotask(action);
  const renderInto = (container: Element, component: ReactNode) => {
    const root = createRoot(container);
    flushSync(() => root.render(createElement(ThemeProvider, { theme: options.theme }, component)));
    buttonRoots.push(root);
  };

  driverInstance = driverFactory({
    animate: !options.reducedMotion,
    allowClose: true,
    doneBtnText: "Close",
    popoverClass: "tutorial-popover",
    showProgress: options.steps.length > 1,
    onPopoverRender: (popover: TutorialPopover) => {
      clearButtonRoots();
      // Remove Driver.js's native button reset so the MUI variants stay intact.
      popover.footer.className = "tutorial-popover-footer";
      popover.footerButtons.className = "tutorial-popover-navigation";

      const closeButtonRoot = document.createElement("span");
      popover.closeButton.replaceWith(closeButtonRoot);
      renderInto(
        closeButtonRoot,
        createElement(
          Fragment,
          null,
          createElement(TutorialPopoverStyles),
          createElement(
            TutorialPopoverCloseButton,
            { "aria-label": "Close", onClick: () => schedule(() => driverInstance.destroy()), size: "small" },
            createElement(CloseIcon),
          ),
        ),
      );

      const disableButtonRoot = document.createElement("span");
      disableButtonRoot.className = "tutorial-disable-all-button";
      popover.footer.insertBefore(disableButtonRoot, popover.footerButtons);
      renderInto(
        disableButtonRoot,
        createElement(
          TutorialDisableButton,
          {
            onClick: () => {
              if (disableRequested) return;
              disableRequested = true;
              schedule(options.onDisableTutorials);
            },
            size: "small",
            variant: "text",
          },
          "Disable all tutorials",
        ),
      );

      popover.footerButtons.replaceChildren();
      renderInto(
        popover.footerButtons,
        createElement(
          TutorialButtonGroup,
          null,
          createElement(
            TutorialButton,
            {
              disabled: popover.previousButton.disabled,
              onClick: () => schedule(() => driverInstance.movePrevious()),
              size: "small",
              variant: "outlined",
            },
            buttonLabel(popover.previousButton, "Previous"),
          ),
          createElement(
            TutorialButton,
            { onClick: () => schedule(() => driverInstance.moveNext()), size: "small", variant: "contained" },
            buttonLabel(popover.nextButton, "Next"),
          ),
        ),
      );
    },
    steps: options.steps.map((cue) => ({
      element: options.targets.get(cue.targetId),
      popover: {
        title: cue.title,
        description: cue.description,
        side: "bottom",
        align: "start",
      },
    })),
    onDestroyed: () => {
      clearButtonRoots();
      options.onDestroyed();
    },
  });

  return driverInstance;
}
